/**
 * Piezas compartidas de la vista pública de stream.
 * Se usan en los dos modos (compacto y pantalla completa) para que no se desincronicen.
 *
 * Estética: esquinas con gradiente + glow, medallón de pozo, barra de tensión animada
 * y ticker en vivo. Todo derivado de tokens (nada de colores sueltos).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { PublicTicket } from '@thepit/contracts';
import { useT } from '../lib/i18n.js';
import { formatMoney, formatOdds, formatPercentBp } from '../lib/format.js';

export type Currency = { code: string; symbol: string; decimals: number };
type Corner = 'red' | 'blue';

const cornerColor = (which: Corner): string =>
  which === 'red' ? 'var(--pit-corner-red)' : 'var(--pit-corner-blue)';

/** Iniciales del peleador, ignorando apodos entre comillas. */
function initials(name: string): string {
  const clean = name.replace(/["'«»].*?["'«»]/g, ' ').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? '?';
  const b = parts[1]?.[0] ?? '';
  return (a + b).toUpperCase();
}

/** Avatar hexagonal con iniciales (marca de esquina). */
export function CornerAvatar({
  which,
  name,
  size = 56,
}: {
  which: Corner;
  name: string;
  size?: number;
}): JSX.Element {
  const color = cornerColor(which);
  return (
    <span
      aria-hidden
      className="pit-hex grid shrink-0 place-items-center"
      style={{
        height: size,
        width: size,
        background: `linear-gradient(160deg, color-mix(in srgb, ${color} 62%, transparent), color-mix(in srgb, ${color} 10%, var(--pit-bg-sunken)))`,
        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 65%, transparent), 0 0 22px -8px ${color}`,
      }}
    >
      <span
        className="pit-mono font-[var(--pit-weight-black)] text-white"
        style={{ fontSize: size * 0.36, letterSpacing: '0.02em' }}
      >
        {initials(name)}
      </span>
    </span>
  );
}

/** Pastilla de cuota con indicador de movimiento (nueva función). */
export function OddsPill({
  oddsBp,
  deltaBp = 0,
  size = 'md',
}: {
  oddsBp: number;
  deltaBp?: number;
  size?: 'md' | 'lg';
}): JSX.Element {
  const moved = deltaBp !== 0;
  const up = deltaBp > 0;
  return (
    <span
      className={[
        'inline-flex items-baseline gap-1 rounded-[var(--pit-radius-md)] border px-2 py-0.5',
        'pit-mono tabular-nums',
        moved
          ? up
            ? 'border-[var(--pit-state-success)] text-[var(--pit-state-success)]'
            : 'border-[var(--pit-state-danger)] text-[var(--pit-state-danger)]'
          : 'border-[var(--pit-border-strong)] text-[var(--pit-text-secondary)]',
      ].join(' ')}
      style={{ fontSize: size === 'lg' ? 'clamp(1.1rem,2.2vw,1.9rem)' : 'clamp(0.85rem,1.4vw,1.15rem)' }}
    >
      {formatOdds(oddsBp)}
      {moved && (
        <span aria-hidden className="text-[0.7em] font-[var(--pit-weight-bold)]">
          {up ? '▲' : '▼'}
          {formatOdds(Math.abs(deltaBp))}
        </span>
      )}
    </span>
  );
}

/** Banda de esquina: tarjeta con gradiente, avatar, récord y pozo. */
export function CornerBand({
  which,
  name,
  crew,
  record,
  poolCents,
  oddsBp,
  betCount,
  currency,
  isFavorite = false,
  size = 'sm',
}: {
  which: Corner;
  name: string;
  crew: string | null;
  record?: { wins: number; losses: number; draws: number } | null;
  poolCents: number;
  oddsBp: number;
  betCount: number;
  currency: Currency;
  isFavorite?: boolean;
  size?: 'sm' | 'lg';
}): JSX.Element {
  const t = useT();
  const color = cornerColor(which);

  // Movimiento de la cuota: compara con el valor previo (nueva función).
  const prevOdds = useRef(oddsBp);
  const [delta, setDelta] = useState(0);
  useEffect(() => {
    if (oddsBp !== prevOdds.current) {
      setDelta(oddsBp - prevOdds.current);
      prevOdds.current = oddsBp;
      const timer = window.setTimeout(() => setDelta(0), 4000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [oddsBp]);

  const big = size === 'lg';

  return (
    <section
      className={`pit-corner pit-anim-up relative flex flex-col`}
      style={{ '--corner': color } as CSSProperties}
      aria-label={`${t(which === 'red' ? 'fight.red' : 'fight.blue')}: ${name}`}
    >
      <div className="pit-corner-bar" />
      <div className={`flex flex-1 flex-col gap-2 ${big ? 'p-[clamp(0.9rem,1.8vw,1.6rem)]' : 'p-3.5'}`}>
        <div className="flex items-center gap-3">
          <CornerAvatar which={which} name={name} size={big ? 60 : 46} />
          <p
            className="pit-mono min-w-0 flex-1 truncate text-[var(--pit-text-3xs)] uppercase tracking-[clamp(0.12em,0.35vw,0.35em)]"
            style={{ color }}
          >
            {t(which === 'red' ? 'fight.red' : 'fight.blue')}
            {crew && <span className="ml-2 text-[var(--pit-text-muted)]">{crew}</span>}
          </p>
          {isFavorite && (
            <span
              className="pit-mono shrink-0 rounded-[var(--pit-radius-sm)] border px-1.5 py-0.5 text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)]"
              style={{ borderColor: color, color }}
            >
              {t('fight.favorite')}
            </span>
          )}
        </div>

        <h2
          className={`break-words font-[var(--pit-weight-black)] uppercase leading-[var(--pit-leading-tight)] ${
            big ? 'text-[clamp(1.25rem,2.9vw,2.5rem)]' : 'text-[clamp(1.05rem,2.1vw,1.65rem)]'
          }`}
        >
          {name}
        </h2>

        <div className="mt-auto flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className={`pit-figure ${big ? 'text-[clamp(1.6rem,4vw,3.4rem)]' : 'text-[clamp(1.2rem,2.6vw,2.1rem)]'}`}>
            {formatMoney(poolCents, currency)}
          </span>
          <OddsPill oddsBp={oddsBp} deltaBp={delta} size={big ? 'lg' : 'md'} />
          <span className="pit-label ml-auto flex items-center gap-2">
            {record && (
              <>
                <span>
                  {t('settings.record')} {record.wins}-{record.losses}-{record.draws}
                </span>
                <span aria-hidden>·</span>
              </>
            )}
            <span>
              {betCount} {t('fight.bets')}
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

/** Medallón central: pozo total, comisión y estado. */
export function PoolPill({
  totalCents,
  rakeBp,
  currency,
  size = 'sm',
  statusLabel,
}: {
  totalCents: number;
  rakeBp: number;
  currency: Currency;
  size?: 'sm' | 'lg';
  statusLabel?: string;
}): JSX.Element {
  const t = useT();
  const big = size === 'lg';
  return (
    <div
      className={`pit-medallion flex flex-col items-center justify-center gap-0.5 ${
        big ? 'px-[clamp(1rem,2.5vw,2.5rem)] py-4' : 'px-4 py-2.5'
      }`}
    >
      {statusLabel && (
        <span className="pit-mono mb-1 rounded-[var(--pit-radius-full)] border border-[var(--pit-accent-amber)] px-2 py-0.5 text-[var(--pit-text-3xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-accent-amber)]">
          {statusLabel}
        </span>
      )}
      <span className="pit-label">{t('obs.totalPool')}</span>
      <span
        className="pit-figure text-[var(--pit-accent-amber)] text-shadow-gold"
        style={{ fontSize: big ? 'clamp(1.7rem,3.6vw,3rem)' : 'clamp(1.25rem,2.6vw,2rem)' }}
      >
        {formatMoney(totalCents, currency)}
      </span>
      <span className="pit-label">
        {t('fight.rake')} {formatPercentBp(rakeBp)}
      </span>
    </div>
  );
}

/** Barra de tensión animada: proporción del pozo entre ambos lados. */
export function TensionBar({ redPct }: { redPct: number }): JSX.Element {
  const t = useT();
  const bluePct = 100 - redPct;
  return (
    <div className="pit-card px-3 py-2.5">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="pit-mono text-[var(--pit-text-xs)] font-[var(--pit-weight-bold)] text-[var(--pit-corner-red)] tabular-nums">
          {redPct}%
        </span>
        <span className="pit-label">{t('fight.tension')}</span>
        <span className="pit-mono text-[var(--pit-text-xs)] font-[var(--pit-weight-bold)] text-[var(--pit-corner-blue)] tabular-nums">
          {bluePct}%
        </span>
      </div>
      <div
        role="meter"
        aria-valuenow={redPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('a11y.poolChart')}
        className="relative h-3 w-full overflow-hidden rounded-[var(--pit-radius-full)] bg-[var(--pit-bg-sunken)] shadow-[inset_0_1px_3px_rgba(0,0,0,0.6)]"
      >
        <div
          className="h-full transition-[width] duration-[var(--pit-duration-slow)] ease-[var(--pit-ease-standard)]"
          style={{
            width: `${redPct}%`,
            background: 'linear-gradient(90deg, var(--pit-corner-red), var(--pit-accent-amber))',
            boxShadow: '0 0 16px -2px var(--pit-corner-red)',
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/25"
        />
      </div>
    </div>
  );
}

/** Ticker de últimos boletos (nombres ya enmascarados por la API). */
export function Ticker({
  tickets,
  currency,
  rows = 8,
  title,
}: {
  tickets: PublicTicket[];
  currency: Currency;
  rows?: number;
  title?: string;
}): JSX.Element {
  const t = useT();
  return (
    <section aria-label={title ?? t('hud.ticker')} className="pit-card overflow-hidden">
      <header className="flex items-center gap-2 border-b border-[var(--pit-border-subtle)] px-3 py-2">
        <span className="pit-live-dot" aria-hidden />
        <span className="pit-label">{title ?? t('hud.ticker')}</span>
      </header>
      {tickets.length === 0 ? (
        <p className="px-3 py-4 text-[var(--pit-text-xs)] text-[var(--pit-text-muted)]">{t('hud.noTickets')}</p>
      ) : (
        <ul className="flex flex-col">
          {tickets.slice(0, rows).map((ticket) => {
            const color = cornerColor(ticket.corner);
            return (
              <li
                key={ticket.receiptCode}
                className="pit-anim-ticker flex items-center gap-2 border-b border-[var(--pit-border-subtle)]/60 px-3 py-1.5 text-[var(--pit-text-xs)] last:border-b-0"
                style={{ borderLeft: `2px solid ${color}` }}
              >
                <span aria-hidden className="pit-mono text-[0.7rem]" style={{ color }}>
                  {ticket.corner === 'red' ? '▲' : '▼'}
                </span>
                <span className="truncate">{ticket.citizenLabel}</span>
                <span className="pit-mono hidden text-[var(--pit-text-muted)] sm:inline">
                  {ticket.receiptCode}
                </span>
                <span className="pit-mono ml-auto font-[var(--pit-weight-bold)] tabular-nums">
                  {formatMoney(ticket.stakeCents, currency)}
                </span>
                <span className="pit-mono w-14 text-right tabular-nums text-[var(--pit-text-muted)]">
                  {ticket.oddsBp ? formatOdds(ticket.oddsBp) : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
